import './chat-drafts.ts';
/** Authenticated fetch SSE. State and cursor live only as long as the signed-in AppShell. */
import { useEffect, useEffectEvent, useRef, useSyncExternalStore } from 'react';
import { useQueryClient, type Query, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { apiFetch, apiGetEventStream, ApiProblem } from './api.ts';
import { clearChatMemory } from './chat-memory.ts';
import { useSession } from './session.tsx';
import { CHAT_API, chatKeys } from './chat-api.ts';

export interface SseMessage { event: string; id: string | null; data: string }
export async function readChatSse(response: Response, onMessage: (message: SseMessage) => void, signal: AbortSignal): Promise<void> {
  if (!response.ok || !response.body) throw new Error('Verbindung unterbrochen');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      if (buffer.length > 1_000_000) throw new Error('Ungültige Verbindung');
      let boundary;
      while ((boundary = /\r?\n\r?\n/.exec(buffer))) {
        const block = buffer.slice(0, boundary.index);
        buffer = buffer.slice(boundary.index + boundary[0].length);
        let event = 'message'; let id: string | null = null; const data: string[] = [];
        for (const line of block.split(/\r?\n/)) {
          if (line.startsWith(':')) continue;
          const colon = line.indexOf(':');
          const field = colon === -1 ? line : line.slice(0, colon);
          const raw = colon === -1 ? '' : line.slice(colon + 1);
          const value = raw.startsWith(' ') ? raw.slice(1) : raw;
          if (field === 'event') event = value;
          if (field === 'id' && !value.includes('\0')) id = value;
          if (field === 'data') data.push(value);
        }
        if (data.length) onMessage({ event, id, data: data.join('\n') });
      }
    }
  } finally { await reader.cancel().catch(() => {}); reader.releaseLock(); }
}

interface LiveState { connected: boolean; connecting: boolean; denied: boolean; revision: number; online: ReadonlySet<string>; typing: ReadonlyMap<string, { userId: string; channelId: string; expires: number }> }
const initial: LiveState = { connected: false, connecting: true, denied: false, revision: 0, online: new Set(), typing: new Map() };
let state = initial;
const listeners = new Set<() => void>();
const publish = (next: LiveState) => { state = next; for (const listener of listeners) listener(); };
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
export function useChatLive() { return useSyncExternalStore(subscribe, () => state, () => initial); }

/** Clear revoked content immediately; active observers must still resolve to the server's 404. */
export async function reconcileChatMembership(qc: QueryClient, channelIds: ReadonlySet<unknown>): Promise<void> {
  const former = (query: Query) => query.queryKey[0] === 'chat' && ['messages', 'channel'].includes(String(query.queryKey[1])) && !channelIds.has(query.queryKey[2]);
  qc.removeQueries({ predicate: (query) => former(query) && !query.isActive() });
  await qc.resetQueries({ predicate: (query) => former(query) && query.isActive() });
}

/**
 * Invalidate after a live change without losing it. TanStack lets a first fetch (no data yet) keep running on invalidation
 * and its result then clears the invalidation: a change committed after that request left would never be shown (a
 * conversation opening while new messages arrive would miss the later ones). Such a fetch is restarted; a refetch of
 * existing data is already cancelled and restarted by `invalidateQueries`.
 */
export function refreshChatQueries(qc: QueryClient, queryKey: QueryKey): Promise<void> {
  const firstFetch = (query: Query) => query.state.data === undefined && query.state.fetchStatus !== 'idle';
  if (!qc.getQueryCache().findAll({ queryKey, predicate: firstFetch }).length) return qc.invalidateQueries({ queryKey });
  return qc.cancelQueries({ queryKey, predicate: firstFetch }).then(() => qc.invalidateQueries({ queryKey }));
}

export function useChatConnection(): void {
  const { me, getToken, auth } = useSession();
  const qc = useQueryClient();
  // A successful recheck of the same session replaces getToken without revoking access.
  // Read its latest value on reconnect; only identity/auth lifecycle changes tear down memory.
  const openStream = useEffectEvent((signal: AbortSignal, cursor: string | null) =>
    apiGetEventStream(getToken, `${CHAT_API}/stream`, { signal, cursor }));
  useEffect(() => {
    let stopped = false;
    let cursor: string | null = null;
    let controller: AbortController | null = null;
    let reconnect: ReturnType<typeof setTimeout> | undefined;
    let delay = 1000;
    publish(initial);
    const changed = new Set<string>();
    let invalidation: ReturnType<typeof setTimeout> | undefined;
    const invalidate = () => {
      invalidation = undefined;
      for (const id of changed) {
        void refreshChatQueries(qc, chatKeys.messages(id));
        void refreshChatQueries(qc, chatKeys.channel(id));
      }
      changed.clear();
      void refreshChatQueries(qc, chatKeys.channels);
      void refreshChatQueries(qc, ['chat', 'direct']);
    };
    const onMessage = (message: SseMessage) => {
      if (stopped) return;
      const data = JSON.parse(message.data) as Record<string, unknown>;
      if (message.event === 'change' && typeof data.channelId === 'string') {
        changed.add(data.channelId);
        if (!invalidation) invalidation = setTimeout(invalidate, 0);
      }
      if (message.event === 'reset') void refreshChatQueries(qc, chatKeys.all);
      if (message.event === 'channels' && Array.isArray(data.channelIds)) {
        const ids = new Set(data.channelIds);
        clearChatMemory(me.id, ids);
        // Forget former conversations immediately; private data must not linger after leaving.
        void reconcileChatMembership(qc, ids);
        // Dateiansichten (24.7) hängen an der Datei-ID, nicht am Kanal: alle verwerfen, der Neuabruf prüft die Mitgliedschaft.
        qc.removeQueries({ predicate: (query) => query.queryKey[0] === 'chat' && query.queryKey[1] === 'file' });
        void refreshChatQueries(qc, chatKeys.channels);
        void refreshChatQueries(qc, ['chat', 'direct']);
      }
      if (message.event === 'presence' && Array.isArray(data.userIds)) publish({ ...state, online: new Set(data.userIds.filter((id): id is string => typeof id === 'string')) });
      if (message.event === 'typing' && typeof data.userId === 'string' && typeof data.channelId === 'string') {
        const typing = new Map(state.typing);
        const key = `${data.channelId}:${data.userId}`;
        if (data.typing === true) typing.set(key, { userId: data.userId, channelId: data.channelId, expires: Date.now() + 5000 });
        else typing.delete(key);
        publish({ ...state, typing });
      }
      // Only advance after accepting the event. Decimal strings avoid precision loss.
      if (message.id !== null) cursor = message.id;
      publish({ ...state, connected: true, connecting: false });
    };
    async function connect() {
      if (stopped) return;
      clearTimeout(reconnect);
      controller?.abort();
      const active = new AbortController();
      controller = active;
      try {
        const response = await openStream(active.signal, cursor);
        if (stopped || controller !== active || active.signal.aborted) return;
        delay = 1000;
        publish({ ...state, denied: false });
        void refreshChatQueries(qc, chatKeys.all);
        await readChatSse(response, (message) => {
          if (controller === active && !active.signal.aborted) onMessage(message);
        }, active.signal);
      } catch (error) {
        // A network failure preserves drafts. A confirmed access denial must forget private data.
        if (!stopped && controller === active && !active.signal.aborted && error instanceof ApiProblem && error.status === 403) {
          cursor = null;
          clearTimeout(invalidation); invalidation = undefined; changed.clear();
          clearChatMemory(me.id);
          publish({ ...initial, connecting: false, denied: true, revision: state.revision + 1 });
          await qc.cancelQueries({ queryKey: chatKeys.all });
          qc.removeQueries({ queryKey: chatKeys.all });
        }
      }
      finally {
        if (!stopped && controller === active) {
          publish({ ...state, connected: false, connecting: false, online: new Set(), typing: new Map() });
          reconnect = setTimeout(() => void connect(), delay);
          delay = Math.min(delay * 2, 15_000);
        }
      }
    }
    const resume = () => { if (document.visibilityState === 'visible' && navigator.onLine) void connect(); };
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    const expire = setInterval(() => {
      const typing = new Map([...state.typing].filter(([, entry]) => entry.expires > Date.now()));
      if (typing.size !== state.typing.size) publish({ ...state, typing });
    }, 500);
    const unsubscribeLogout = auth.onSignedOut(() => { clearChatMemory(me.id); qc.removeQueries({ queryKey: chatKeys.all }); });
    void connect();
    return () => {
      unsubscribeLogout();
      stopped = true; controller?.abort(); clearTimeout(reconnect); clearTimeout(invalidation); clearInterval(expire);
      clearChatMemory(me.id);
      window.removeEventListener('online', resume); document.removeEventListener('visibilitychange', resume);
      publish(initial);
    };
  }, [me.id, auth, qc]);
}

export function useChatTyping(channelId: string) {
  const { getToken } = useSession();
  useEffect(() => () => { void apiFetch(getToken, `${CHAT_API}/channels/${channelId}/typing`, { method: 'POST', body: { typing: false } }).catch(() => {}); }, [channelId, getToken]);
  const last = useRef(0);
  return (typing: boolean) => {
    if (typing && Date.now() - last.current < 2000) return;
    last.current = typing ? Date.now() : 0;
    void apiFetch(getToken, `${CHAT_API}/channels/${channelId}/typing`, { method: 'POST', body: { typing } }).catch(() => {});
  };
}
